/**
 * Chat-error telemetry — report shapes plus the pure helpers that make a
 * gateway error safe and useful to ship.
 *
 * See infra/observability/terminal/frontend-errors/README.md for the model.
 */

/** Which layer produced the failure. */
export type ChatErrorSource =
  /** A run the user started failed — the `agent`/`chat` error frame. */
  | 'run'
  /** The gateway (or the proxy in front of it) answered with an error. */
  | 'gateway'
  /** The client refused to send — the failure never reached the gateway. */
  | 'client';

/**
 * Deliberately coarse, so a new gateway wording lands in an existing bucket
 * instead of minting a series. `unknown` is expected to be non-empty.
 */
export type ChatErrorCategory =
  | 'auth'
  | 'rate_limit'
  | 'timeout'
  | 'context_overflow'
  | 'payload_too_large'
  | 'model_unavailable'
  | 'gateway_unreachable'
  | 'session_busy'
  | 'tool_failure'
  | 'unknown';

/**
 * Whose problem this is. Split from `category` so a provider capacity event
 * (`rate_limit`) can't read as the harness regressing — the stability KPI is
 * the `harness` slice alone.
 *
 * Two judgement calls, first to revisit if the numbers look wrong:
 * `context_overflow` is `harness` (staying inside the window is its job), and
 * `timeout` stays `unknown` (a stalled model call and a dead socket read
 * identically).
 */
export type ChatErrorAttribution =
  | 'harness'
  | 'provider'
  | 'network'
  | 'client'
  | 'unknown';

/**
 * Every category, as a set. Exported so a host classifier's answer can be
 * validated against the same source the buckets come from rather than a
 * second list that could drift.
 */
export const CHAT_ERROR_CATEGORIES: ReadonlySet<ChatErrorCategory> = new Set([
  'auth',
  'rate_limit',
  'timeout',
  'context_overflow',
  'payload_too_large',
  'model_unavailable',
  'gateway_unreachable',
  'session_busy',
  'tool_failure',
  'unknown',
]);

const ATTRIBUTION: Readonly<Record<ChatErrorCategory, ChatErrorAttribution>> = {
  rate_limit: 'provider',
  model_unavailable: 'provider',
  context_overflow: 'harness',
  session_busy: 'harness',
  tool_failure: 'harness',
  gateway_unreachable: 'network',
  payload_too_large: 'client',
  auth: 'client',
  timeout: 'unknown',
  unknown: 'unknown',
};

export function attributionFor(
  category: ChatErrorCategory,
): ChatErrorAttribution {
  return ATTRIBUTION[category];
}

/**
 * `pseudonymous` ⇒ `id` is a salted hash. Pseudonymization, not anonymity —
 * the salt ships in the bundle, as any client-side hash must.
 */
export interface ChatErrorUser {
  readonly id: string;
  readonly pseudonymous: boolean;
}

/**
 * Which agent backend produced this, so a migration is a filter rather than an
 * unexplained step in the failure rate. From the connect handshake, else
 * `TerminalConfig.harness`, else omitted.
 */
export interface HarnessInfo {
  readonly name?: string;
  readonly version?: string;
  readonly protocol?: number;
}

/**
 * Conditions the run ran under. Fields are omitted rather than guessed when
 * the provider can't know them (a background chat has no live phase reading).
 * No message content, nothing personal.
 */
export interface ChatRunContext {
  /** What the run was doing when it broke — "Thinking", a tool name. */
  readonly phase?: string;
  /** Ms from the send to the failure. */
  readonly elapsedMs?: number;
  /** Which turn of the conversation this was. */
  readonly turnIndex?: number;
  readonly hadAttachments?: boolean;
  /** Context-window fill at failure, 0–1. The direct read on `context_overflow`. */
  readonly contextFill?: number;
  /** Some of the answer had already streamed — a partial failure. */
  readonly streamed?: boolean;
  /** This send was a retry of a chat that had just failed. */
  readonly isRetry?: boolean;
  /** Socket generation — how many reconnects this tab has been through. */
  readonly connectionGeneration?: number;
}

/** One chat failure, ready to ship. `message` is already scrubbed. */
export interface ChatErrorReport {
  readonly source: ChatErrorSource;
  readonly category: ChatErrorCategory;
  readonly message: string;
  readonly sessionId?: string;
  readonly runId?: string;
  readonly modelId?: string;
  readonly attribution: ChatErrorAttribution;
  /** The wire's own failure code, when it sent one. Stable across wordings. */
  readonly code?: string;
  readonly harness?: HarnessInfo;
  /**
   * This client sent the run. `false` ⇒ no `chat_run` will ever pair with
   * this failure (a cron turn, another device, a run from before a reload),
   * so counting it in a rate inflates the numerator against a denominator it
   * was never part of. Filter on it — see the README's KPI query.
   */
  readonly owned?: boolean;
  /** Who hit it, when the telemetry tier allows saying. */
  readonly user?: ChatErrorUser;
  readonly run?: ChatRunContext;
  readonly timestamp: number;
}

/**
 * A run that ENDED — the denominator that turns a failure count into a rate.
 *
 * Only for runs THIS client sent: a cron turn or another device's run would
 * pad the denominator with runs whose failures never reach the numerator.
 */
export interface RunOutcomeReport {
  readonly outcome: 'ok' | 'error' | 'cancelled';
  /** Ms from send to terminator. */
  readonly durationMs: number;
  readonly sessionId?: string;
  readonly runId?: string;
  readonly modelId?: string;
  /** Set when `outcome` is `error`. */
  readonly category?: ChatErrorCategory;
  readonly attribution?: ChatErrorAttribution;
  readonly harness?: HarnessInfo;
  readonly user?: ChatErrorUser;
  readonly run?: ChatRunContext;
  /**
   * Stripped for the `minimal` tier. Carried on the report because the SINK
   * has to know too: the tier gate gets the report right, but the transport
   * adds its own envelope, and a per-tab session id plus a user-agent links
   * reports as effectively as any field the gate removed.
   */
  readonly minimal?: true;
  readonly timestamp: number;
}

/**
 * `code` outranks the message: wording changes between backends and
 * providers, a code is a contract.
 */
export interface ChatErrorSignal {
  readonly message?: string | null;
  /** Unvalidated wire data — anything but a string is ignored, not trusted. */
  readonly code?: unknown;
}

/**
 * Fragments matched against the normalized code, so `SESSION_LOCKED`,
 * `session.locked` and `ERR_SESSION_LOCK_HELD` all carry `SESSION_LOCK`.
 * Ordered, first match wins, most specific first.
 */
const CODE_FRAGMENTS: ReadonlyArray<
  readonly [ChatErrorCategory, readonly string[]]
> = [
  [
    'context_overflow',
    [
      'CONTEXT_LENGTH',
      'CONTEXT_WINDOW',
      'CONTEXT_OVERFLOW',
      'TOKEN_LIMIT',
      'PROMPT_TOO_LONG',
    ],
  ],
  [
    'payload_too_large',
    ['TOO_LARGE', 'PAYLOAD_LIMIT', 'MESSAGE_SIZE', 'REQUEST_SIZE'],
  ],
  [
    'rate_limit',
    [
      'RATE_LIMIT',
      'RATELIMIT',
      'TOO_MANY_REQUESTS',
      'QUOTA',
      'THROTTL',
      'BUDGET_EXCEEDED',
    ],
  ],
  [
    'model_unavailable',
    [
      'MODEL_NOT_FOUND',
      'MODEL_UNAVAILABLE',
      'MODEL_UNSUPPORTED',
      'UNKNOWN_MODEL',
      'OVERLOADED',
      'NO_API_KEY',
      'PROVIDER_UNAVAILABLE',
    ],
  ],
  [
    'auth',
    [
      'UNAUTH',
      'FORBIDDEN',
      'PERMISSION_DENIED',
      'INVALID_TOKEN',
      'TOKEN_MISMATCH',
      'TOKEN_EXPIRED',
      'EXPIRED_TOKEN',
      'INVALID_CREDENTIAL',
      'PAIRING',
      'NOT_PAIRED',
      'ACCESS_DENIED',
    ],
  ],
  ['timeout', ['TIMEOUT', 'TIMED_OUT', 'DEADLINE']],
  [
    'session_busy',
    [
      'SESSION_LOCK',
      'SESSION_BUSY',
      'SESSION_IN_USE',
      'LOCK_HELD',
      'ALREADY_RUNNING',
      'ALREADY_IN_PROGRESS',
      'CONFLICT',
    ],
  ],
  [
    'tool_failure',
    [
      'TOOL_FAIL',
      'TOOL_ERROR',
      'TOOL_EXECUTION',
      'MCP_ERROR',
      'MCP_FAIL',
      'HOOK_FAIL',
      'HOOK_ERROR',
    ],
  ],
  [
    'gateway_unreachable',
    [
      'UNREACHABLE',
      'UPSTREAM',
      'DISCONNECT',
      'CONNECTION_',
      'NETWORK',
      'SERVICE_UNAVAILABLE',
      'BAD_GATEWAY',
      'GATEWAY_',
    ],
  ],
];

function normalizeCode(code: string): string {
  return code.toUpperCase().replace(/[^A-Z0-9]+/gu, '_');
}

/**
 * HTTP statuses, matched EXACTLY rather than as fragments.
 *
 * The message patterns already know these, but a gateway that puts the status
 * in `code` and something generic in the message landed in `unknown`. Exact
 * match, because a `429` fragment would also read inside `4290` — the same
 * boundary bug the message patterns had.
 */
const CODE_STATUSES: Readonly<Record<string, ChatErrorCategory>> = {
  '401': 'auth',
  '403': 'auth',
  '413': 'payload_too_large',
  '429': 'rate_limit',
  '502': 'gateway_unreachable',
  '503': 'gateway_unreachable',
  '504': 'timeout',
  '529': 'model_unavailable',
};

/**
 * A wire `code` as text, or `null` when there is nothing usable.
 *
 * `unknown` in, because the wire is only CAST to a shape in the frame handler.
 * A gateway sending `code: 429` arrives as a NUMBER — the shape `CODE_STATUSES`
 * is written for — and the earlier string-only guard threw on `.trim()`, so the
 * TypeError escaped the frame handler and left no error banner and the spinner
 * up forever. Guarding here fixed the throw but bailed on the number, which
 * dropped `429` from both the bucket and the report. Coerced, not rejected.
 */
function codeText(code: unknown): string | null {
  if (typeof code === 'number') {
    return Number.isFinite(code) ? String(code) : null;
  }
  if (typeof code !== 'string') return null;
  return code.trim().length === 0 ? null : code;
}

/**
 * Classify by structured code alone. `null`, not `unknown`, on no match — so
 * the caller falls through to the message instead of stopping at a code that
 * said nothing.
 */
export function categorizeByCode(code: unknown): ChatErrorCategory | null {
  const text = codeText(code);
  if (text == null) return null;
  const normal = normalizeCode(text);
  const status = CODE_STATUSES[normal];
  if (status != null) return status;
  for (const [category, fragments] of CODE_FRAGMENTS) {
    if (fragments.some((fragment) => normal.includes(fragment))) {
      return category;
    }
  }
  return null;
}

/**
 * The fallback when no code arrives. Written from HTTP statuses and provider
 * phrasing rather than one backend's wording, so a harness swap doesn't
 * silently reclassify everything. Ordered, first match wins — they overlap.
 */
const CATEGORY_PATTERNS: ReadonlyArray<readonly [ChatErrorCategory, RegExp]> = [
  [
    'auth',
    /\b(401|403|unauth\w*|forbidden|auth(entication)? (failed|error|required)|invalid (token|credential|api key|signature)|expired (token|credential|session)|token mismatch|not paired|pair(ing)? required|permission denied)\b/iu,
  ],
  // `\w*` on the open-ended stems rather than a bare `\b`, which would have
  // broken `throttling` and `rate-limited`. Without the closing boundary this
  // matched `quotation` (via `quota`) and `429` inside `4290`.
  [
    'rate_limit',
    /\b(429|rate.?limit\w*|too many requests|quota|budget exceeded|throttl\w*)\b/iu,
  ],
  [
    'context_overflow',
    /\b(context (window|length|limit)|prompt is too long|maximum context|too many tokens)\b/iu,
  ],
  [
    'payload_too_large',
    /\b(413|payload too large|(message|request|frame) (is )?too large|exceeds the maximum size)\b/iu,
  ],
  // Before `timeout` on purpose. A lock that timed out is contention the
  // HARNESS owns, and `timeout` attributes to `unknown` — so with `timeout`
  // first, the `lock timeout` alternative below could never be reached and
  // the more informative bucket was unreachable by construction.
  [
    'session_busy',
    /\b(session (is )?(locked|busy|in use)|already (running|in progress)|lock (held|acquired|timeout))\b/iu,
  ],
  ['timeout', /\b(timeout|timed out|deadline exceeded|504)\b/iu],
  [
    'model_unavailable',
    /\b(529|overloaded|model (not found|unavailable|is not supported)|unknown model|no api key found for provider|does not have access to model)\b/iu,
  ],
  [
    'gateway_unreachable',
    /\b(502|503|bad gateway|gateway (unreachable|unavailable|not connected)|econnrefused|connection (refused|reset|closed)|websocket (closed|error)|network error|failed to fetch)\b/iu,
  ],
  [
    'tool_failure',
    /\b(tool (call|execution|use) failed|mcp (server|tool) (error|failed)|hook (failed|error))\b/iu,
  ],
];

/** Code first, message patterns second. */
export function categorizeChatError(
  input: string | ChatErrorSignal | null | undefined,
): ChatErrorCategory {
  const signal: ChatErrorSignal =
    typeof input === 'string' ? { message: input } : (input ?? {});
  const byCode = categorizeByCode(signal.code);
  if (byCode != null) return byCode;
  const message = signal.message;
  if (message == null || message.trim().length === 0) return 'unknown';
  for (const [category, pattern] of CATEGORY_PATTERNS) {
    if (pattern.test(message)) return category;
  }
  return 'unknown';
}

export const MAX_CHAT_ERROR_MESSAGE_CHARS = 300;

/**
 * How much more than the kept length the scrubber will look at. Redactions
 * shorten the text, so the head has to be longer than the final cap for the
 * cap to be the thing that truncates.
 */
const SCRUB_INPUT_HEADROOM = 16;

/**
 * Each targets a SHAPE, not a known secret — these messages are relayed from a
 * gateway and nothing here can enumerate what they might quote. Order matters:
 * JWT before the generic hex rule (so a token goes whole, not just its
 * signature), `key: value` before the address rules.
 */
const REDACTIONS: ReadonlyArray<readonly [RegExp, string]> = [
  // JWTs — the DIME identity token, the gateway device token, Clerk's.
  //
  // The dots are OPTIONAL, which reads over-broad and is not. Requiring two
  // meant a token the input slice above cut in half — a `header.claims`
  // prefix, subject and email intact — matched nothing and shipped: the
  // redactions ahead of it compress hard enough (`<jwt>` for ~90 characters)
  // to pull that tail back inside the 300-char cap, so "truncated anyway"
  // does not hold. `eyJ` plus six base64url characters is a base64 JSON
  // header and nothing else; there is no prose to lose here.
  [/\beyJ[A-Za-z0-9_-]{6,}(\.[A-Za-z0-9_-]*){0,2}/gu, '<jwt>'],
  // Cookies, whole. The value is opaque and often several credentials, so
  // everything after the separator goes, to end of line.
  [/\b(set-cookie|cookie)\b"?\s*[:=]\s*[^\r\n]*/giu, '$1: <redacted>'],
  // `key: value`, `key=value`, and the JSON form `"key": "value"` — the quote
  // may sit before the separator and before the value, and an auth scheme may
  // sit between them. Without the scheme branch, `Authorization: Bearer <tok>`
  // redacted the word `Bearer` and shipped the token.
  //
  // The key may be PREFIXED. A leading `\b` matched none of `refresh_token`,
  // `device_token`, `id_token` or `sessionToken`, because `_` is a word
  // character and a letter-to-letter join is no boundary at all — so the most
  // common refresh-credential shapes shipped intact. `[\w-]*` in front lets
  // the stem carry its own prefix, and the separator before it is preserved
  // through `$1` rather than eaten.
  [
    /(^|[^\w-])([\w-]*(?:authorization|api[_-]?key|access[_-]?token|token|secret|password|credential))\b"?\s*[:=]\s*"?(?:bearer|basic|token)?\s*\S+/giu,
    '$1$2: <redacted>',
  ],
  // A bare scheme + credential, with no key in front of it.
  [/\b(bearer|basic)\s+[A-Za-z0-9._~+/=-]{8,}/giu, '$1 <redacted>'],
  // Account and session ids that ship WITHOUT a `key:` label — a Clerk user
  // id (`user_…`), an org or session id — quoted in relayed prose like
  // `account user_2Nx… is not authorized`. The pseudonym gate strips the id
  // from `user`, but the same id echoed inside a message went out raw.
  [/\b(?:user|org|sess|client)_[A-Za-z0-9]{16,64}\b/gu, '<id>'],
  // Bare vendor keys with a recognisable prefix but no label: `sk-…` /
  // `pk-…` provider keys, an AWS access-key id.
  [/\b(?:sk|pk|rk)[-_][A-Za-z0-9_-]{8,80}\b/giu, '<redacted>'],
  [/\b(?:AKIA|ASIA)[0-9A-Z]{12,64}\b/gu, '<redacted>'],
  // Every part BOUNDED. Unbounded `+` quantifiers backtrack quadratically over
  // a long unbroken run of `[\w.+-]` — a base64url blob quoted back in an
  // oversize-frame error — which measured 3.3s at 50k characters on the main
  // thread, in a scrubber that runs while a chat is failing. The bounds are
  // past any real address and cost nothing.
  [/[\w.+-]{1,64}@[\w-]{1,63}(\.[\w-]{1,63}){1,8}/gu, '<email>'],
  // Addresses, device key ids, hashes. 16 chars is past anything in prose.
  [/\b0x[0-9a-fA-F]{8,}\b/gu, '<hex>'],
  [/\b[0-9a-fA-F]{16,}\b/gu, '<hex>'],
  // Query strings and fragments carry one-time credentials on this stack
  // (grant redemption, sign-in hand-offs). Keep the path, drop the rest.
  [/(https?:\/\/[^\s?#]*)[?#]\S*/gu, '$1'],
];

/**
 * The entropy BACKSTOP, kept apart from the named rules above because it is
 * the one rule that guesses.
 *
 * A bare high-entropy run none of the named shapes caught — an unlabelled
 * token echoed in prose. It must MIX letters and digits, so an ordinary long
 * word or a pure-letter repeat is left alone; every part is bounded
 * (lookaheads included) so this stays linear on a long blob. Runs the named
 * rules already replaced are `<…>` by now and can't re-match.
 *
 * Applied to free-form MESSAGES only, never to `code`. Guessing is the right
 * trade in prose, where anything can appear, and the wrong one in a field that
 * is a low-cardinality enum by convention: a conventional code long enough to
 * reach 24 characters while carrying a digit —`HTTP_429_TOO_MANY_REQUESTS`,
 * `RATE_LIMIT_EXCEEDED_TIER2_RETRY` — was being reported as `<token>`, which
 * cost the `code` dimension exactly the codes that carry the most information,
 * on the harness-agnostic path that bets on a new backend emitting
 * conventional vocabulary. A case- or underscore-based exemption was tried
 * first and is not the answer: it still ate `ERR_HTTP2_STREAM_CANCEL_0x8`
 * (mixed case, from the hex literal) while newly exempting a lowercase token.
 * `code` still gets every NAMED rule, so a JWT, an `sk-…`, a bearer token, an
 * account id or a hex blob stuffed into a code is redacted as before.
 */
const ENTROPY_REDACTION: readonly [RegExp, string] = [
  /\b(?=[A-Za-z0-9_-]{0,79}[0-9])(?=[A-Za-z0-9_-]{0,79}[A-Za-z])[A-Za-z0-9_-]{24,80}\b/gu,
  '<token>',
];

/**
 * Empty input yields `'Unknown error'` — a blank value in the backend is
 * indistinguishable from a dropped field.
 */
export function scrubChatErrorMessage(
  message: string | null | undefined,
  maxChars: number = MAX_CHAT_ERROR_MESSAGE_CHARS,
): string {
  if (message == null) return 'Unknown error';
  // Bound the INPUT before any pattern runs. Bounding each pattern fixes the
  // one that was measured; this bounds the ones that aren't, and the cost of a
  // relayed frame that quotes a whole request body. The slice is generous
  // enough that nothing a report would have kept is lost — everything past it
  // was going to be truncated anyway — and dropping a credential unscrubbed is
  // safe in the one direction that matters: it never leaves the device.
  let out = message.slice(0, maxChars * SCRUB_INPUT_HEADROOM);
  for (const [pattern, replacement] of [...REDACTIONS, ENTROPY_REDACTION]) {
    out = out.replace(pattern, replacement);
  }
  out = out.replace(/\s+/gu, ' ').trim();
  if (out.length === 0) return 'Unknown error';
  return out.length > maxChars ? `${out.slice(0, maxChars - 1)}…` : out;
}

/**
 * A wire `code` as it may be REPORTED: every named redaction, no entropy
 * backstop. See `ENTROPY_REDACTION` for why the backstop is excluded here.
 *
 * Classification still reads the RAW code, so narrowing this cannot change
 * which bucket a failure lands in — only what the report says the code was.
 */
export function scrubWireCode(code: string): string {
  let out = code.slice(0, MAX_CHAT_ERROR_MESSAGE_CHARS);
  for (const [pattern, replacement] of REDACTIONS) {
    out = out.replace(pattern, replacement);
  }
  return out.replace(/\s+/gu, ' ').trim();
}

/** The single constructor, so every emit site scrubs and buckets identically. */
export function buildChatErrorReport(input: {
  readonly source: ChatErrorSource;
  readonly message: string | null | undefined;
  /** Unvalidated wire data; kept as text when it is a string or a number. */
  readonly code?: unknown;
  /**
   * Pre-resolved bucket, for a caller that ran its own classifier. Passed in
   * so a run's recorded failure and its reported failure cannot end up in
   * different buckets.
   */
  readonly category?: ChatErrorCategory;
  readonly harness?: HarnessInfo | null;
  readonly owned?: boolean;
  readonly sessionId?: string | null;
  readonly runId?: string | null;
  readonly modelId?: string | null;
  readonly user?: ChatErrorUser | null;
  readonly run?: ChatRunContext | null;
  readonly timestamp?: number;
}): ChatErrorReport {
  const category =
    input.category ??
    categorizeChatError({ message: input.message, code: input.code });
  return {
    source: input.source,
    category,
    attribution: attributionFor(category),
    message: scrubChatErrorMessage(input.message),
    // `code` is wire data too — classified raw above, but shipped through the
    // same scrubber as `message` so a backend that stuffs a token into its
    // error code can't route around the redactions. A conventional code
    // (`429`, `SESSION_LOCKED`) matches no rule and passes through unchanged.
    ...(codeText(input.code) != null
      ? { code: scrubWireCode(codeText(input.code)!) }
      : {}),
    ...(input.harness != null ? { harness: input.harness } : {}),
    ...(input.owned != null ? { owned: input.owned } : {}),
    ...(input.sessionId != null ? { sessionId: input.sessionId } : {}),
    ...(input.runId != null ? { runId: input.runId } : {}),
    ...(input.modelId != null ? { modelId: input.modelId } : {}),
    ...(input.user != null ? { user: input.user } : {}),
    ...(input.run != null && Object.keys(input.run).length > 0
      ? { run: input.run }
      : {}),
    timestamp: input.timestamp ?? Date.now(),
  };
}

/**
 * Strip to what `minimal` permits: outcome, timing, failure shape — nothing
 * tying two reports to a person or a conversation. A last step over a built
 * report, not a separate build path, so a field added later is dropped here
 * by default rather than leaking.
 */
export function toMinimalRunOutcome(
  report: RunOutcomeReport,
): RunOutcomeReport {
  return {
    minimal: true,
    outcome: report.outcome,
    durationMs: report.durationMs,
    ...(report.category != null ? { category: report.category } : {}),
    ...(report.attribution != null ? { attribution: report.attribution } : {}),
    ...(report.modelId != null ? { modelId: report.modelId } : {}),
    // Survives `minimal`: it identifies a BUILD, not a person.
    ...(report.harness != null ? { harness: report.harness } : {}),
    timestamp: report.timestamp,
  };
}

/**
 * In the bundle, which is the limit of any browser-side hash — but it does
 * stop someone reading the backend from mapping a report to a customer.
 */
const PSEUDONYM_SALT = 'dime-terminal/telemetry/v1';

/**
 * Salted SHA-256 of an account, truncated to 16 hex chars. `null` without Web
 * Crypto (a non-secure origin): the report then carries no user reference at
 * all rather than something weaker.
 */
export async function pseudonymFor(accountId: string): Promise<string | null> {
  const subtle = globalThis.crypto?.subtle;
  if (subtle == null) return null;
  try {
    const data = new TextEncoder().encode(`${PSEUDONYM_SALT}:${accountId}`);
    const digest = await subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest).slice(0, 8), (b) =>
      b.toString(16).padStart(2, '0'),
    ).join('');
  } catch {
    return null;
  }
}
